import { Navigate } from "react-router-dom";
import { Button } from "@tellescope/ui";
import { LoadingScreen } from "./LoadingScreen";
import { CallingProvider } from "../providers/CallingProvider";
import { VideoCallingProvider } from "../providers/VideoCallingProvider";
import { useAuth } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation("auth");

  const { session, isAuthenticated, organizations, organizationError, isLoadingOrganizations, logout } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (session?.userInfo?.requiresMFA) {
    return <Navigate to="/mfa" replace />;
  }

  if (isLoadingOrganizations) {
    return <LoadingScreen message="Loading your workspace..." />;
  }

  if (organizationError || !organizations?.length) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-2">{t("Failed to load workspace")}</p>
          <p className="text-muted-foreground text-sm mb-4">{String(organizationError)}</p>
          <Button variant="outline" onClick={() => logout()}>
            {t("Logout")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CallingProvider>
      <VideoCallingProvider>{children}</VideoCallingProvider>
    </CallingProvider>
  );
};
